import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import debounce from 'lodash/debounce';

import { GetStaffResponse, ListStaffRequest } from '@vyce/core/src/api/types';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

type Props = {
  selectedCompanyId: string;
  getStaffRequest?: (
    companyId: string,
    payload: ListStaffRequest
  ) => Promise<AxiosResponse<GetStaffResponse>>;
};

export const useFetchTeamManagerCandidates = ({ selectedCompanyId, getStaffRequest }: Props) => {
  const [staff, setStaff] = useState<{ uuid: string; name: string }[]>([]);
  const [staffSubstring, setStaffSubstring] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const { handleServerError } = useContext(NotificationContext);
  const isMountedRef = useRef(true);

  const fetchStaff = useCallback(
    async (substring: string) => {
      if (!selectedCompanyId || !getStaffRequest) return;
      try {
        setLoading(true);
        const { data } = await getStaffRequest(selectedCompanyId, {
          limit: 1000,
          offset: 0,
          substring,
          roles: ['Teams:Manager'],
        });
        // The debounced request can resolve after the dialog unmounts — avoid setting state then.
        if (!isMountedRef.current) return;
        setStaff(
          data.items.map(item => ({
            uuid: item.user_id,
            name: `${item.first_name} ${item.last_name}`,
          }))
        );
        setLoading(false);
      } catch (e) {
        if (!isMountedRef.current) return;
        setLoading(false);
        handleServerError(e);
      }
    },
    [selectedCompanyId, getStaffRequest, handleServerError]
  );

  const debouncedFetchStaff = useMemo(() => debounce(fetchStaff, 500), [fetchStaff]);

  useEffect(() => {
    debouncedFetchStaff(staffSubstring);
    return () => debouncedFetchStaff.cancel();
  }, [staffSubstring, debouncedFetchStaff]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { staff, setStaffSubstring, loading };
};
